<template>
  <v-container class="px-0">
    <v-skeleton-loader
      v-if="isLoading"
      type="button@4"
      class="mb-2 d-flex"
      style="gap: 6px;"
    />
    <v-tabs
      v-else
      v-model="activeTab"
      show-arrows
      background-color="transparent"
      color="primary"
    >
      <v-tab href="#ALL">Tous ({{ store.state.entities.length }})</v-tab>
      <v-tab
        v-for="(tradeUnit, tradeUnitIndex) in tradeUnits"
        :key="tradeUnitIndex"
        :href="`#${tradeUnit.uuid}`"
      >
        {{ tradeUnit.name }} ({{ tradeUnit.entities.length }})
      </v-tab>
    </v-tabs>

    <v-row>
      <v-col cols="12" md="6">
        <v-card
          elevation="0"
          outlined
          rounded="lg"
          class="mt-2"
        >
          <v-skeleton-loader
            v-if="isLoading"
            type="image"
            height="40"
            class="ma-2 rounded-lg"
          />
          <v-text-field
            v-else
            v-model="searchEntities"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="Recherche"
            transition="scale-transition"
            class="rounded-lg"
          />
          <v-list
            dense
            class="flex-grow-1 overflow-y-auto"
            style="max-height: 400px; min-height: 400px;"
          >
            <template v-if="isLoading">
              <v-skeleton-loader type="list-item@8" />
            </template>

            <template v-else-if="filteredEntitiesByTradeUnit.length === 0">
              <div class="text-center grey--text mt-10">Aucun résultat</div>
            </template>

            <template v-else>
              <v-list-item
                v-for="(entity, entityIndex) in filteredEntitiesByTradeUnit"
                :key="entityIndex"
                @click="selectEntity(entity)"
              >
                <v-list-item-action class="mr-2">
                  <v-icon color="success">mdi-plus-circle-outline</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>{{ entity.name }}</v-list-item-title>
                  <v-list-item-subtitle
                    v-if="activeTab !== 'ALL' && entity.tradeUnit?.name"
                  >
                    {{ entity.tradeUnit.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card
          elevation="0"
          outlined
          rounded="lg"
          class="mt-2"
        >
          <v-skeleton-loader
            v-if="isLoading || isParentLoading"
            type="image"
            height="40"
            class="ma-2 rounded-lg"
          />
          <v-text-field
            v-else
            v-model="searchSelectedEntities"
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            placeholder="Recherche"
            transition="scale-transition"
            class="rounded-lg"
          />
          <v-list
            dense
            class="flex-grow-1 overflow-y-auto"
            style="max-height: 400px; min-height: 400px;"
          >
            <template v-if="isLoading">
              <v-skeleton-loader type="list-item@8" />
            </template>

            <template v-else-if="filteredSelectedEntities.length === 0">
              <div class="text-center grey--text mt-10">Aucun résultat</div>
            </template>

            <template v-else>
              <v-list-item
                v-for="(selectedEntity, selectedEntityIndex) in filteredSelectedEntities"
                :key="selectedEntityIndex"
                :to="!selectedEntity.isRemovable ? { name: 'offer', params: { contentTypeLabel: 'offres', mode: 'editer', uuid: selectedEntity.offerUuid } } : null"
                @click="selectedEntity.isRemovable ? removeEntity(selectedEntity) : null"
              >
                <v-list-item-action class="mr-2">
                  <v-icon :color="selectedEntity.isRemovable ? 'error' : 'success'">
                    {{ selectedEntity.isRemovable ? 'mdi-minus-circle-outline' : 'mdi-check-circle' }}
                  </v-icon>
                </v-list-item-action>

                <v-list-item-content>
                  <v-divider v-if="value.length && value.length === selectedEntityIndex" />
                  <v-list-item-title>{{ selectedEntity.name }}</v-list-item-title>
                  <v-list-item-subtitle
                    v-if="selectedEntity.tradeUnit"
                  >
                    {{ selectedEntity.tradeUnit.name }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import store from '@/store';
import { requests } from '@/mixins/requests';

import { filterListBySearch } from '@/configs/helper';

const props = defineProps({
  isParentLoading: {
    type: Boolean,
    default: false
  },
  value: {
    type: Array,
    default: () => []
  },
  dispatchedOffers: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['input']);

const isLoading = ref(false);
const activeTab = ref('ALL');
const searchEntities = ref('');
const searchSelectedEntities = ref('');
const tradeUnits = ref([]);

const getEntitiesByDispatchedOffers = computed(() => {
  if (!store.state.entities.length || !props.dispatchedOffers?.length) return [];

  return props.dispatchedOffers.map(offer => ({
    ...offer.entity,
    offerUuid: offer.uuid,
    isRemovable: false
  }));
});

const filteredEntitiesByTradeUnit = computed(() => {
  let list = activeTab.value === 'ALL'
    ? store.state.entities
    : tradeUnits.value.find(tradeUnit => tradeUnit.uuid === activeTab.value)?.entities || [];

  // Exclude already selected (Draft) AND already dispatched
  const allSelectedUuids = new Set([
    ...props.value.map(entity => entity.uuid),
    ...getEntitiesByDispatchedOffers.value.map(entity => entity.uuid)
  ]);

  list = list.filter(entity => !allSelectedUuids.has(entity.uuid));

  return filterListBySearch(list, searchEntities.value);
});

const filteredSelectedEntities = computed(() => (filterListBySearch([...props.value, ...getEntitiesByDispatchedOffers.value], searchSelectedEntities.value)));

const extractTradeUnits = entities => {
  const map = new Map();

  entities.forEach(entity => {
    if (entity.tradeUnit) {
      const { uuid, name } = entity.tradeUnit;

      if (!map.has(uuid)) {
        map.set(uuid, {
          name,
          uuid,
          entities: []
        });
      }

      map.get(uuid).entities.push(entity);
    }
  });

  tradeUnits.value = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const getEntitiesData = async () => {
  if (store.state.entities.length) {
    extractTradeUnits(store.state.entities);
    return;
  }

  isLoading.value = true;
  try {
    const response = await requests.getEntityItems({ key: 'MAIN_ENTITIES' });

    if (response.success && response.data) {
      store.commit('setEntities', response.data);
      extractTradeUnits(response.data);
    } else {
      store.commit('setSnackbar', { text: 'Contenu introuvable', color: 'error' });
    }
  } catch (error) {
    console.error('Failed to load entities', error);
  } finally {
    isLoading.value = false;
  }
};
const selectEntity = entity => {
  const newSelection = [...props.value, { ...entity, isRemovable: true }];
  newSelection.sort((a, b) => a.name.localeCompare(b.name));
  emit('input', newSelection);
};

const removeEntity = entity => {
  const newSelection = props.value.filter(e => e.uuid !== entity.uuid);
  newSelection.sort((a, b) => a.name.localeCompare(b.name));
  emit('input', newSelection);
};

onMounted(async () => {
  await getEntitiesData();
});
</script>
